import { Component, createRef } from "react";
import { connect } from "react-redux";
import classNames from "classnames";
import { compose, defaultProps, withState, lifecycle } from "recompose";
import { noop, isEmpty, get, find, isString } from "lodash";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import { Tooltip as ReactTooltip } from "react-tooltip";
import MenuButton from "react-md/lib/Menus/MenuButton";
import ListItem from "react-md/lib/Lists/ListItem";
import { Button, FontIcon, Card, Divider } from "react-md";

import { setDialog } from "../actions/dialog-actions";
import { setImageEditor } from "../actions/app-actions";
import ComponentLoader from "./loaders/component-loader";
import { withTranslation } from "react-i18next";

const TOOLTIP_ID = "image-editor-toolbar-icon-tooltip";

const menuItem = (
  {
    key,
    divider,
    menuItems,
    icon,
    tooltip,
    label,
    className,
    containerClassName,
    ...item
  },
  i
) =>
  divider ? (
    <div key={i} className={`divider ${className}`} />
  ) : !isEmpty(menuItems) ? (
    <div className={containerClassName}>
      <MenuButton
        key={`${tooltip}-${key}`}
        id={`image-editor-menubutton-${tooltip}`}
        flat
        label={label}
        buttonChildren={icon}
        position="tr"
        data-tooltip-content={tooltip}
        data-tooltip-id={TOOLTIP_ID}
        className={`button ${className}`}
      >
        {menuItems.map(({ label, value }) => (
          <ListItem
            key={label}
            primaryText={label}
            onClick={() => item.onClick(value)}
          />
        ))}
      </MenuButton>
    </div>
  ) : (
    <Button
      key={`${tooltip}-${key}`}
      icon
      data-tooltip-content={tooltip}
      data-tooltip-id={TOOLTIP_ID}
      className={`button ${className}`}
      {...item}
    >
      {icon}
    </Button>
  );

class ImageEditor extends Component {
  constructor(props) {
    super(props);
    this.cropperRef = createRef();
  }

  crop() {
    const canvas = this.cropperRef.current.getCroppedCanvas();
    if (canvas.height > 1 && canvas.width > 1) {
      this.cropperRef.current.replace(canvas.toDataURL());
    } else {
      this.props.setDialog("Info", {
        content: (
          <h2 className="text-center margin-none">
            {this.props.t("cropAreaTooSmall")}
          </h2>
        ),
        autoClose: true,
        autoCloseTime: 2000,
        noDialogMenu: true,
        noToolbar: true,
        big: true,
      });
    }
  }

  cropMove() {
    const newIsCrop = !this.props.isCrop;
    this.props.setCrop(newIsCrop);
    this.cropperRef.current.setDragMode(newIsCrop ? "crop" : "move");
  }

  zoomIn() {
    this.cropperRef.current.zoom(0.1);
  }

  zoomOut() {
    this.cropperRef.current.zoom(-0.1);
  }

  rotateLeft() {
    this.cropperRef.current.rotate(-90);
  }

  rotateRight() {
    this.cropperRef.current.rotate(90);
  }

  scaleHoriz() {
    const newScale = [this.props.scale[0] * -1, this.props.scale[1]];
    this.props.setScale(newScale);
    this.cropperRef.current.scale(newScale[0], newScale[1]);
  }

  scaleVert() {
    const newScale = [this.props.scale[0], this.props.scale[1] * -1];
    this.props.setScale(newScale);
    this.cropperRef.current.scale(newScale[0], newScale[1]);
  }

  render() {
    const {
      src,
      aspectRatio,
      aspectRatioOptions,
      setAspectRatio,
      onClose,
      guides,
      setGuides,
      isCrop,
      cropperState,
      setCropperState,
      setDialog,
      expoId,
      folder,
      menuOpen,
      setMenuOpen,
      loading,
      setLoading,
      type,
      setImageEditor,
      t,
    } = this.props;

    const close = () => {
      setImageEditor(null);
      onClose();
    };

    const saveAction = (className) => ({
      tooltip: t("saveActionTooltip"),
      icon: "save",
      onClick: () => {
        const croppedCavnas = this.cropperRef.current.getCroppedCanvas(
          !type || type === "image/png"
            ? {}
            : {
                fillColor: "#fff",
              }
        );

        const dataUrl = croppedCavnas.toDataURL(type);

        croppedCavnas.toBlob(
          (blob) =>
            setDialog("ImageEditorSave", {
              expoId,
              folder,
              blob,
              dataUrl,
              onSave: () => {
                setLoading(false);
                close();
              },
              onError: () => {
                setLoading(false);
                setDialog("Info", {
                  content: (
                    <h2 className="text-center margin-none invalid">
                      {t("failedToSaveFile")}
                    </h2>
                  ),
                  autoClose: true,
                  autoCloseTime: 3000,
                  noDialogMenu: true,
                  noToolbar: true,
                  big: true,
                });
              },
              onSubmitStart: () => {
                setMenuOpen(false);
                t("saving");
              },
            }),
          type
        );
      },
      className,
    });
    const resetAction = (className) => ({
      tooltip: t("resetActionTooltip"),
      icon: "cached",
      onClick: () =>
        setDialog("ConfirmDialog", {
          title: t("resetActionDialogTitle"),
          content: (
            <div>
              <p>{t("resetActionDialogContent1")}</p>
              <div className="flex-row-nowrap flex-center">
                <FontIcon className="color-red">priority_high</FontIcon>
                <p>
                  <strong style={{ fontSize: "0.9em" }}>
                    {t("resetActionDialogContent2")}
                  </strong>
                </p>
              </div>
            </div>
          ),
          onSubmit: () => {
            setMenuOpen(false);
            setLoading(true);
            setCropperState(!cropperState);
          },
        }),
      className,
    });
    const guidesAction = (className) => ({
      key: guides,
      tooltip: guides
        ? t("hideGuidesActionTooltip")
        : t("showGuidesActionTooltip"),
      icon: guides ? "grid_off" : "grid_on",
      onClick: () =>
        setDialog("ConfirmDialog", {
          title: guides
            ? t("hideGuidesDialogTitle")
            : t("showGuidesDialogTitle"),
          content: (
            <div>
              <p>{t("guidesDialogContent1")}</p>
              <div className="flex-row-nowrap flex-center">
                <FontIcon className="color-red">priority_high</FontIcon>
                <p>
                  <strong style={{ fontSize: "0.9em" }}>
                    {t("guidesDialogContent2")}
                  </strong>
                </p>
              </div>
            </div>
          ),
          onSubmit: () => {
            setMenuOpen(false);
            setLoading(true);
            setGuides(!guides);
          },
        }),
      className,
    });
    const cropMoveAction = (className) => ({
      tooltip: isCrop ? t("movementModeTooltip") : t("cropModeTooltip"),
      icon: isCrop ? "zoom_out_map" : "crop_free",
      onClick: this.cropMove.bind(this),
      className,
    });
    const aspectRatioAction = (className) => ({
      key: aspectRatio,
      tooltip: t("aspectRatioActionTooltip"),
      label: get(
        find(aspectRatioOptions, ({ value }) => aspectRatio === value),
        "label",
        "Vlastní"
      ),
      icon: "aspect_ratio",
      onClick: (value) => setAspectRatio(value),
      menuItems: aspectRatioOptions,
      className,
      containerClassName: "image-editor-aspect-ratio",
    });
    const cropAction = (className) => ({
      tooltip: t("cropActionTooltip"),
      icon: "crop",
      onClick: this.crop.bind(this),
      className,
    });
    const zoomInAction = (className) => ({
      tooltip: t("zoomInActionTooltip"),
      icon: "zoom_in",
      onClick: this.zoomIn.bind(this),
      className,
    });
    const zoomOutAction = (className) => ({
      tooltip: t("zoomOutActionTooltip"),
      icon: "zoom_out",
      onClick: this.zoomOut.bind(this),
      className,
    });
    const rotateLeftAction = (className) => ({
      tooltip: t("rotateLeftActionTooltip"),
      icon: "rotate_left",
      onClick: this.rotateLeft.bind(this),
      className,
    });
    const rotateRightAction = (className) => ({
      tooltip: t("rotateRightActionTooltip"),
      icon: "rotate_right",
      onClick: this.rotateRight.bind(this),
      className,
    });
    const swapHorizAction = (className) => ({
      tooltip: t("swapHorizActionTooltip"),
      icon: "swap_horiz",
      onClick: this.scaleHoriz.bind(this),
      className,
    });
    const swapVertAction = (className) => ({
      tooltip: t("swapVertActionTooltip"),
      icon: "swap_vert",
      onClick: this.scaleVert.bind(this),
      className,
    });
    const menuAction = (className) => ({
      tooltip: t("menuActionTooltip"),
      icon: "menu",
      onClick: () => setMenuOpen(true),
      className,
    });
    const closeAction = (className) => ({
      tooltip: t("closeActionTooltip"),
      icon: "close",
      onClick: () =>
        setDialog("ConfirmDialog", {
          title: t("closeDialogTitle"),
          content: (
            <div>
              <p>{t("closeDialogContent1")}</p>
              <div className="flex-row-nowrap flex-center">
                <FontIcon className="color-red">priority_high</FontIcon>
                <p>
                  <strong style={{ fontSize: "0.9em" }}>
                    {t("closeDialogContent2")}
                  </strong>
                </p>
              </div>
            </div>
          ),
          onSubmit: close,
          closeBefore: true,
        }),
      className,
    });

    const divider = (className) => ({ divider: true, className });

    return (
      <div className={"image-editor"}>
        <div className="image-editor-toolbar">
          <h3
            className={classNames("image-editor-title", {
              "screen-small-min": !loading,
            })}
          >
            {t("imageEditorLabel")}
          </h3>
          {loading
            ? [divider(), closeAction(), divider()].map(menuItem)
            : [
                divider("screen-small-min screen-big-max"),
                menuAction("screen-big-max"),
                divider("screen-big-min"),
                saveAction("screen-big-min"),
                resetAction("screen-big-min"),
                divider("screen-big-min"),
                guidesAction("screen-big-min"),
                divider("screen-big-min"),
                cropMoveAction("screen-big-min"),
                divider("screen-big-min"),
                aspectRatioAction("screen-big-min"),
                divider("screen-big-min"),
                cropAction("screen-big-min"),
                divider(),
                zoomInAction(),
                zoomOutAction(),
                divider(),
                rotateLeftAction(),
                rotateRightAction(),
                divider("screen-big-min"),
                swapHorizAction("screen-big-min"),
                swapVertAction("screen-big-min"),
                closeAction("!ml-auto !mr-3"),
              ].map(menuItem)}
          {loading && (
            <h3 className="image-editor-loading-title">
              {isString(loading) ? loading : t("loadingLabel")}
            </h3>
          )}
        </div>
        <div className="image-editor-container">
          <div className="image-editor-inner">
            <Cropper
              {...{
                key: `${cropperState}-${guides}`,
                src,
                ref: this.cropperRef,
                aspectRatio,
                guides,
                viewMode: 1,
                style: { height: "100%", width: "100%" },
                ready: () => setLoading(false),
              }}
            />
          </div>
          {loading && (
            <div className="component-loader-container">
              <ComponentLoader />
            </div>
          )}
        </div>
        {menuOpen && (
          <Card className="menu-dialog">
            <div className="padding">
              <div className="flex-row flex-space-between flex-center">
                <h4 className="margin-left-small margin-bottom-none">Menu</h4>
                <Button
                  icon
                  data-tooltip-content="Zavřít menu"
                  data-tooltip-id={TOOLTIP_ID}
                  onClick={() => setMenuOpen(false)}
                >
                  close
                </Button>
              </div>
            </div>
            <Divider />
            <div className="padding">
              <div className="flex-row flex-centered">
                {[
                  saveAction(),
                  closeAction(),
                  resetAction(),
                  guidesAction(),
                  cropMoveAction(),
                  aspectRatioAction(),
                  cropAction(),
                  swapHorizAction(),
                  swapVertAction(),
                ].map(menuItem)}
              </div>
            </div>
          </Card>
        )}
        <ReactTooltip
          key={`${guides}-${aspectRatio}-${isCrop}-${menuOpen}-${loading}`}
          id={TOOLTIP_ID}
          variant="dark"
          float={false}
        />
      </div>
    );
  }
}

export default compose(
  defaultProps({
    open: false,
    onClose: noop,
    aspectRatioOptions: [
      { label: "16:10", value: 16 / 10 },
      { label: "16:9", value: 16 / 9 },
      { label: "11:8", value: 11 / 8 },
      { label: "7:3", value: 7 / 3 },
      { label: "6:5", value: 6 / 5 },
      { label: "5:4", value: 5 / 4 },
      { label: "5:3", value: 5 / 3 },
      { label: "4:3", value: 4 / 3 },
      { label: "3:2", value: 3 / 2 },
      { label: "1:1", value: 1 },
      { label: "Vlastní", value: null },
    ],
  }),
  lifecycle({
    UNSAFE_componentWillReceiveProps({ open }) {
      if (open && !this.props.open) {
        document.body.style.overflow = "hidden";
      }
    },
    componentWillUnmount() {
      document.body.style.overflow = "auto";
    },
  }),
  connect(null, { setDialog, setImageEditor }),
  withState("loading", "setLoading", true),
  withState("menuOpen", "setMenuOpen", false),
  withState("isCrop", "setCrop", true),
  withState("guides", "setGuides", true),
  withState("cropperState", "setCropperState", true),
  withState("scale", "setScale", [1, 1]),
  withState("aspectRatio", "setAspectRatio", 16 / 9),
  withTranslation("expo-editor", { keyPrefix: "imageEditor" })
)(ImageEditor);
